import { PriceChange, PriceChangeStats } from '../types';

/**
 * Calculate comprehensive statistics from price change data
 */
export const calculatePriceChangeStats = (changes: PriceChange[]): PriceChangeStats => {
  if (changes.length === 0) {
    return {
      totalChanges: 0,
      totalIncreases: 0,
      totalDecreases: 0,
      avgPriceChange: 0,
      avgPercentageChange: 0,
      mostChangedProduct: null,
      topUserByChanges: null,
      changesByType: {
        restaurant: 0,
        delivery: 0,
        online: 0,
        desk: 0,
        all: 0
      },
      changesByDay: {}
    };
  }

  // Count increases and decreases
  const increases = changes.filter(c => c.new_price > c.old_price);
  const decreases = changes.filter(c => c.new_price < c.old_price);

  // Calculate average price change
  const totalPriceChange = changes.reduce((sum, c) => sum + (c.new_price - c.old_price), 0);
  const avgPriceChange = totalPriceChange / changes.length;

  // Calculate average percentage change
  const percentageChanges = changes
    .filter(c => c.old_price > 0)
    .map(c => ((c.new_price - c.old_price) / c.old_price) * 100);
  const avgPercentageChange = percentageChanges.length > 0 
    ? percentageChanges.reduce((a, b) => a + b, 0) / percentageChanges.length 
    : 0;

  // Find most changed product
  const productCounts: Record<string, { name: string; count: number }> = {};
  changes.forEach(c => {
    if (!productCounts[c.product_id]) {
      productCounts[c.product_id] = { name: c.product_name, count: 0 };
    }
    productCounts[c.product_id].count++;
  });
  const mostChangedProduct = Object.values(productCounts).sort((a, b) => b.count - a.count)[0] || null;

  // Find top user by changes
  const userCounts: Record<string, { name: string; count: number }> = {};
  changes.forEach(c => {
    if (!userCounts[c.user_id]) {
      userCounts[c.user_id] = { name: c.user_name, count: 0 };
    }
    userCounts[c.user_id].count++;
  });
  const topUserByChanges = Object.values(userCounts)
    .filter(u => u.name)
    .sort((a, b) => b.count - a.count)[0] || null;

  // Count by price type
  const changesByType = {
    restaurant: changes.filter(c => c.price_type === 'restaurant').length,
    delivery: changes.filter(c => c.price_type === 'delivery').length,
    online: changes.filter(c => c.price_type === 'online').length,
    desk: changes.filter(c => c.price_type === 'desk').length,
    all: changes.filter(c => c.price_type === 'all').length
  };

  // Group by day for chart
  const changesByDay: Record<string, number> = {};
  changes.forEach(c => {
    const date = c.timestamp.split('T')[0];
    changesByDay[date] = (changesByDay[date] || 0) + 1;
  });

  return {
    totalChanges: changes.length,
    totalIncreases: increases.length,
    totalDecreases: decreases.length,
    avgPriceChange,
    avgPercentageChange,
    mostChangedProduct: mostChangedProduct ? {
      product_name: mostChangedProduct.name,
      count: mostChangedProduct.count
    } : null,
    topUserByChanges: topUserByChanges ? {
      user_name: topUserByChanges.name,
      count: topUserByChanges.count
    } : null,
    changesByType,
    changesByDay
  };
};

/**
 * Get unique users from price changes
 */
export const getUniqueUsers = (changes: PriceChange[]): { value: string; label: string }[] => {
  const uniqueUsers = new Map<string, string>();
  changes.forEach(c => {
    if (c.user_id && c.user_name) {
      uniqueUsers.set(c.user_id, c.user_name);
    }
  });
  return Array.from(uniqueUsers.entries()).map(([value, label]) => ({ value, label }));
};

/**
 * Get unique price types from price changes
 */
export const getUniquePriceTypes = (changes: PriceChange[]): { value: string; label: string }[] => {
  const typeLabels: Record<string, string> = {
    restaurant: 'Restaurant',
    delivery: 'Delivery',
    online: 'Online',
    desk: 'Caja',
    all: 'Todos'
  };
  
  const uniqueTypes = new Set<string>();
  changes.forEach(c => {
    if (c.price_type) {
      uniqueTypes.add(c.price_type);
    }
  });
  
  return Array.from(uniqueTypes).map(type => ({
    value: type,
    label: typeLabels[type] || type
  }));
};
